<template>
  <div id="devices-franquias-modal-app">
    <modal
      elem-id="modal_crud_devices_franquias"
      size="small"
      :close-label="t('Fechar')"
      :save-label="t('Salvar')"
      :save-disabled-fn="() => !formRef?.canSave"
      :save-fn="handleSave"
      :title="t('Dispositivo')"
      :show="isVisible"
      @close="closeModal"
      @dismiss="closeModal"
    >
    <!-- Content only renders when modal is visible (prevents API calls on mount) -->
    <template v-if="isVisible">
      <!-- Action Buttons -->
      <div class="text-right" style="margin-bottom: 20px;">
        <Button
          v-if="shouldShowDelete"
          :label="t('Excluir registro')"
          icon="pi pi-trash"
          severity="danger"
          data-testid="devices-franquias-delete-button"
          @click="handleDelete" />
      </div>

      <!-- Form -->
      <devices-franquias-form
        ref="formRef"
        :is-edit-mode="isEditMode"
        @save-success="handleSaveSuccess">
      </devices-franquias-form>
    </template>
    </modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useDevicesFranquias } from '../../composables/useDevicesFranquias';
import { useConfirmAction } from '../../composables/useConfirmAction';
import { log } from '../../utils/DebugLogger';
import DevicesFranquiasForm from './DevicesFranquiasForm.vue';

type DeviceId = number;

interface ModalOpenPayload {
  id?: DeviceId;
  action?: string;
}

interface FormInstance {
  initializeForNew: () => Promise<void>;
  loadForEdit: (deviceId: DeviceId) => Promise<void>;
  resetForm: () => void;
  save: () => Promise<void>;
  canSave: boolean;
}

interface SaveSuccessPayload {
  device_id?: DeviceId;
  device_name?: string;
  provider_type?: string;
}

withDefaults(defineProps<{
  usuarioVersao?: number;
}>(), {
  usuarioVersao: 0
});

// Get i18n composable
const { t } = useI18n();
const { confirmAction } = useConfirmAction();

// Get whatsapp franquias composable
const { removerDevice } = useDevicesFranquias();

// Reactive data
const isVisible = ref(false);
const isEditMode = ref(false);
const formRef = ref<FormInstance | null>(null);
const currentDeviceId = ref<DeviceId>(0);

// Computed properties
const shouldShowDelete = computed(() => {
  return isEditMode.value && currentDeviceId.value > 0;
});

// Methods
const openForNew = async (): Promise<void> => {
  log('DevicesFranquiasModal', 'openForNew', 'Opening modal for new record');
  isVisible.value = true;
  isEditMode.value = false;
  currentDeviceId.value = 0;

  // Wait for the form component to be rendered
  await nextTick();

  // Initialize form for new record
  if (formRef.value) {
    await formRef.value.initializeForNew();
  }
};

const loadForEdit = async (deviceId: DeviceId): Promise<void> => {
  log('DevicesFranquiasModal', 'loadForEdit', 'Opening modal for edit', deviceId);
  isVisible.value = true;
  isEditMode.value = true;
  currentDeviceId.value = deviceId;

  // Wait for the form component to be rendered
  await nextTick();

  // Load data in form
  if (formRef.value) {
    await formRef.value.loadForEdit(deviceId);
  }
};

const closeModal = (): void => {
  log('DevicesFranquiasModal', 'closeModal', 'Closing modal');
  isVisible.value = false;
  isEditMode.value = false;
  currentDeviceId.value = 0;

  // Reset form
  if (formRef.value) {
    formRef.value.resetForm();
  }
};

const handleSave = async (): Promise<void> => {
  log('DevicesFranquiasModal', 'handleSave', 'Saving device');

  if (formRef.value) {
    await formRef.value.save();
  }
};

const handleSaveSuccess = (data: SaveSuccessPayload): void => {
  log('DevicesFranquiasModal', 'handleSaveSuccess', 'Save successful', data);

  const wasNewRecord = !isEditMode.value;
  const savedId = data?.device_id;
  const deviceName = data?.device_name;
  const providerType = data?.provider_type || 'whatsapp';

  // Close modal
  closeModal();

  // Emit refresh event
  eventBus.emit('devices-franquias.refresh');

  // Show success message
  eventBus.emit('globalMessage.success', t('Registro salvo com sucesso!'));

  // Auto-trigger device registration for new WhatsApp records with a device_name
  if (wasNewRecord && providerType === 'whatsapp' && savedId && deviceName && deviceName.trim() !== '') {
    log('DevicesFranquiasModal', 'handleSaveSuccess', 'Auto-triggering device registration', { savedId, deviceName });
    eventBus.emit('modal.device-registration.open', {
      whatsappfranquiaId: savedId,
      deviceName: deviceName
    });
  }
};

// Standard modal open handler (for modal.devices-franquias.open event)
const handleModalOpen = async (data?: ModalOpenPayload): Promise<void> => {
  log('DevicesFranquiasModal', 'handleModalOpen', 'Standard modal open event received', data);

  if (data?.id) {
    // Edit mode
    await loadForEdit(data.id);
  } else {
    // New mode
    await openForNew();
  }
};

const handleDelete = (): void => {
  log('DevicesFranquiasModal', 'handleDelete', 'Deleting device', currentDeviceId.value);

  confirmAction({
    message: t('Tem certeza que deseja remover este registro?'),
    header: t('Confirmar Exclusão'),
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: t('Sim'),
    rejectLabel: t('Não'),
    onAccept: async () => {
      try {
        await removerDevice(currentDeviceId.value);

        eventBus.emit('globalMessage.success', t('Registro removido com sucesso!'));
        closeModal();
        eventBus.emit('devices-franquias.refresh');
      } catch (error) {
        log('DevicesFranquiasModal', 'handleDelete', 'Error deleting record', error);
        eventBus.emit('globalMessage.error', t('Erro ao remover registro'));
      }
    }
  });
};

// Lifecycle
onMounted(() => {
  log('DevicesFranquiasModal', 'mounted', 'Component mounted');

  // Standard modal events (preferred pattern)
  eventBus.on('modal.devices-franquias.open', handleModalOpen);
  eventBus.on('modal.devices-franquias.close', closeModal);
});

onBeforeUnmount(() => {
  log('DevicesFranquiasModal', 'beforeUnmount', 'Component unmounting');

  // Remove standard modal events
  eventBus.off('modal.devices-franquias.open', handleModalOpen);
  eventBus.off('modal.devices-franquias.close', closeModal);
});

// Expose methods for parent components
defineExpose({
  openForNew,
  loadForEdit,
  closeModal
});
</script>

<style scoped>
/* Add any modal-specific styles here */
</style>
